import React, { useState, useEffect } from 'react';
import {
  Building2, Plus, ChevronDown, ChevronRight, FolderKanban,
  Calendar, User, AlertCircle, CheckCircle, Clock, Edit, Trash2, X, Search, Filter
} from 'lucide-react';
import axiosInstance from '../../config/axios';

interface Project {
  id: number;
  client_id: number;
  name: string;
  description?: string;
  status: string;
  priority: string;
  start_date?: string;
  target_end_date?: string;
  long_term_deadline?: string;
  owner?: string;
  services?: string;
  dependent_on?: number;
  notes?: string;
  is_proposal?: boolean;
  created_at: string;
}

interface Client {
  id: number;
  name: string;
  company?: string;
  email?: string;
  phone?: string;
  website?: string;
  notes?: string;
  projects: Project[];
}

export default function ProjectsWithClients() {
  const [clients, setClients] = useState<Client[]>([]);
  const [expandedClients, setExpandedClients] = useState<Set<number>>(new Set());
  const [expandedProjects, setExpandedProjects] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [showAddClientModal, setShowAddClientModal] = useState(false);
  const [showAddProjectModal, setShowAddProjectModal] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');
  const [groupBy, setGroupBy] = useState<'none' | 'status' | 'priority'>('none');

  useEffect(() => {
    loadClientsWithProjects();
  }, []);

  const loadClientsWithProjects = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/clients/with-projects');
      if (response.data.success) {
        setClients(response.data.clients);
      }
    } catch (error) {
      console.error('Failed to load clients with projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleClient = (clientId: number) => {
    const newExpanded = new Set(expandedClients);
    if (newExpanded.has(clientId)) {
      newExpanded.delete(clientId);
    } else {
      newExpanded.add(clientId);
    }
    setExpandedClients(newExpanded);
  };

  const toggleProjectGroup = (groupKey: string) => {
    const newExpanded = new Set(expandedProjects);
    if (newExpanded.has(groupKey)) {
      newExpanded.delete(groupKey);
    } else {
      newExpanded.add(groupKey);
    }
    setExpandedProjects(newExpanded);
  };

  const expandAll = () => {
    const allClientIds = new Set(clients.map(c => c.id));
    setExpandedClients(allClientIds);
    if (groupBy !== 'none') {
      const allGroupKeys = new Set<string>();
      clients.forEach(client => {
        const grouped = groupProjectsBy(client.projects, groupBy);
        Object.keys(grouped).forEach(key => {
          allGroupKeys.add(`${client.id}::${key}`);
        });
      });
      setExpandedProjects(allGroupKeys);
    }
  };

  const collapseAll = () => {
    setExpandedClients(new Set());
    setExpandedProjects(new Set());
  };

  // Filter and group projects
  const filterProjects = (projects: Project[]) => {
    return projects.filter(project => {
      const matchesSearch = !searchTerm ||
        project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        project.description?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === 'all' || project.status === statusFilter;
      const matchesPriority = priorityFilter === 'all' || project.priority === priorityFilter;
      return matchesSearch && matchesStatus && matchesPriority;
    });
  };

  const groupProjectsBy = (projects: Project[], groupByField: 'none' | 'status' | 'priority') => {
    if (groupByField === 'none') return { 'All Projects': projects };

    const groups: Record<string, Project[]> = {};
    projects.forEach(project => {
      const key = groupByField === 'status' ? project.status : project.priority;
      if (!groups[key]) groups[key] = [];
      groups[key].push(project);
    });
    return groups;
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { bg: string; text: string; icon: any }> = {
      planning: { bg: 'bg-gray-100', text: 'text-gray-700', icon: Clock },
      active: { bg: 'bg-blue-100', text: 'text-blue-700', icon: Clock },
      on_hold: { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: AlertCircle },
      completed: { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle },
      cancelled: { bg: 'bg-red-100', text: 'text-red-700', icon: AlertCircle },
    };

    const config = statusConfig[status] || statusConfig.planning;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        <Icon className="h-3 w-3 mr-1" />
        {status.replace('_', ' ')}
      </span>
    );
  };

  const getPriorityBadge = (priority: string) => {
    const priorityConfig: Record<string, { bg: string; text: string }> = {
      low: { bg: 'bg-gray-100', text: 'text-gray-600' },
      medium: { bg: 'bg-blue-100', text: 'text-blue-700' },
      high: { bg: 'bg-orange-100', text: 'text-orange-700' },
      urgent: { bg: 'bg-red-100', text: 'text-red-700' },
    };

    const config = priorityConfig[priority] || priorityConfig.medium;

    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {priority}
      </span>
    );
  };

  const handleAddProject = (clientId: number) => {
    setSelectedClientId(clientId);
    setShowAddProjectModal(true);
  };

  const handleCreateClient = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    try {
      const response = await axiosInstance.post('/clients', {
        name: formData.get('name'),
        company: formData.get('company'),
        email: formData.get('email'),
        phone: formData.get('phone'),
        website: formData.get('website'),
        notes: formData.get('notes'),
      });

      if (response.data) {
        setShowAddClientModal(false);
        loadClientsWithProjects();
      }
    } catch (error) {
      console.error('Failed to create client:', error);
    }
  };

  const handleCreateProject = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    try {
      const response = await axiosInstance.post('/projects', {
        client_id: selectedClientId || formData.get('client_id'),
        name: formData.get('name'),
        description: formData.get('description'),
        status: formData.get('status'),
        priority: formData.get('priority'),
        owner: formData.get('owner'),
        services: formData.get('services'),
        start_date: formData.get('start_date') || null,
        target_end_date: formData.get('target_end_date') || null,
        long_term_deadline: formData.get('long_term_deadline') || null,
        dependent_on: formData.get('dependent_on') || null,
        notes: formData.get('notes'),
      });

      if (response.data) {
        setShowAddProjectModal(false);
        setSelectedClientId(null);
        loadClientsWithProjects();
      }
    } catch (error) {
      console.error('Failed to create project:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Projects</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage projects organized by client
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setShowAddClientModal(true)}
            className="flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition"
          >
            <Building2 className="h-5 w-5 mr-2" />
            Add Client
          </button>
          <button
            onClick={() => setShowAddProjectModal(true)}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            <Plus className="h-5 w-5 mr-2" />
            Add Project
          </button>
        </div>
      </div>

      {/* Filters & Controls */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 space-y-4">
        {/* Search and Basic Filters */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search projects..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
          >
            <option value="all">All Statuses</option>
            <option value="planning">Planning</option>
            <option value="active">Active</option>
            <option value="on_hold">On Hold</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>

          <select
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
          >
            <option value="all">All Priorities</option>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="urgent">Urgent</option>
          </select>

          <select
            value={groupBy}
            onChange={(e) => setGroupBy(e.target.value as any)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
          >
            <option value="none">No Secondary Grouping</option>
            <option value="status">Group by Status</option>
            <option value="priority">Group by Priority</option>
          </select>
        </div>

        {/* Expand/Collapse All */}
        <div className="flex items-center gap-3 pt-2 border-t border-gray-200">
          <span className="text-sm font-medium text-gray-700">View:</span>
          <button
            onClick={expandAll}
            className="px-3 py-1.5 text-xs font-medium text-blue-600 hover:bg-blue-50 rounded transition"
          >
            Expand All
          </button>
          <button
            onClick={collapseAll}
            className="px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-100 rounded transition"
          >
            Collapse All
          </button>
        </div>
      </div>

      {/* Clients List with Expandable Projects */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {clients.map((client) => (
          <div key={client.id} className="border-b border-gray-200 last:border-b-0">
            {/* Client Row */}
            <div
              className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 cursor-pointer transition"
              onClick={() => toggleClient(client.id)}
            >
              <div className="flex items-center flex-1">
                <button className="mr-3 text-gray-400 hover:text-gray-600">
                  {expandedClients.has(client.id) ? (
                    <ChevronDown className="h-5 w-5" />
                  ) : (
                    <ChevronRight className="h-5 w-5" />
                  )}
                </button>
                <div className="h-10 w-10 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
                  <Building2 className="h-5 w-5 text-blue-600" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center">
                    <h3 className="text-lg font-semibold text-gray-900">{client.name}</h3>
                    {client.company && client.company !== client.name && (
                      <span className="ml-2 text-sm text-gray-500">({client.company})</span>
                    )}
                  </div>
                  {client.email && (
                    <p className="text-sm text-gray-500">{client.email}</p>
                  )}
                </div>
                <div className="flex items-center space-x-6 mr-4">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-blue-600">{client.projects.length}</p>
                    <p className="text-xs text-gray-500">Projects</p>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAddProject(client.id);
                    }}
                    className="px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition text-sm font-medium"
                  >
                    <Plus className="h-4 w-4 inline mr-1" />
                    Add Project
                  </button>
                </div>
              </div>
            </div>

            {/* Expanded Projects */}
            {expandedClients.has(client.id) && (() => {
              const filteredProjects = filterProjects(client.projects);
              const groupedProjects = groupProjectsBy(filteredProjects, groupBy);

              if (filteredProjects.length === 0) return null;

              return (
                <div className="bg-gray-50 border-t border-gray-200">
                  {groupBy === 'none' ? (
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Project Name
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Priority
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Owner
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Deadline
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Services
                          </th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {filteredProjects.map((project) => (
                      <tr key={project.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div>
                            <div className="text-sm font-medium text-gray-900">{project.name}</div>
                            {project.description && (
                              <div className="text-sm text-gray-500 line-clamp-1">{project.description}</div>
                            )}
                            {project.is_proposal && (
                              <span className="inline-flex items-center mt-1 px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">
                                Proposal
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(project.status)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getPriorityBadge(project.priority)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {project.owner ? (
                            <div className="flex items-center text-sm text-gray-900">
                              <User className="h-4 w-4 mr-1 text-gray-400" />
                              {project.owner}
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {project.long_term_deadline || project.target_end_date ? (
                            <div className="flex items-center text-sm text-gray-900">
                              <Calendar className="h-4 w-4 mr-1 text-gray-400" />
                              {new Date(project.long_term_deadline || project.target_end_date!).toLocaleDateString()}
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          {project.services ? (
                            <div className="text-sm text-gray-600 line-clamp-1">{project.services}</div>
                          ) : (
                            <span className="text-sm text-gray-400">—</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <div className="flex items-center justify-end space-x-2">
                            <button className="text-blue-600 hover:text-blue-900 p-1 hover:bg-blue-50 rounded">
                              <Edit className="h-4 w-4" />
                            </button>
                            <button className="text-gray-600 hover:text-red-600 p-1 hover:bg-red-50 rounded">
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                  ) : (
                    // Grouped rendering
                    Object.entries(groupedProjects).map(([groupName, groupProjects]) => {
                      const groupKey = `${client.id}::${groupName}`;
                      const isGroupExpanded = expandedProjects.has(groupKey);

                      return (
                        <div key={groupKey} className="border-b border-gray-200 last:border-b-0">
                          <button
                            onClick={() => toggleProjectGroup(groupKey)}
                            className="w-full bg-gray-100 px-6 py-2.5 flex items-center justify-between hover:bg-gray-200 transition"
                          >
                            <h4 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                              {isGroupExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                              {groupName} ({groupProjects.length})
                            </h4>
                          </button>

                          {isGroupExpanded && (
                            <table className="min-w-full divide-y divide-gray-200">
                              <tbody className="bg-white divide-y divide-gray-200">
                                {groupProjects.map((project) => (
                                  <tr key={project.id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4">
                                      <div>
                                        <div className="text-sm font-medium text-gray-900">{project.name}</div>
                                        {project.description && (
                                          <div className="text-sm text-gray-500 line-clamp-1">{project.description}</div>
                                        )}
                                        {project.is_proposal && (
                                          <span className="inline-flex items-center mt-1 px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">
                                            Proposal
                                          </span>
                                        )}
                                      </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      {getStatusBadge(project.status)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      {getPriorityBadge(project.priority)}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      {project.owner ? (
                                        <div className="flex items-center text-sm text-gray-900">
                                          <User className="h-4 w-4 mr-1 text-gray-400" />
                                          {project.owner}
                                        </div>
                                      ) : (
                                        <span className="text-sm text-gray-400">—</span>
                                      )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      {project.long_term_deadline || project.target_end_date ? (
                                        <div className="flex items-center text-sm text-gray-900">
                                          <Calendar className="h-4 w-4 mr-1 text-gray-400" />
                                          {new Date(project.long_term_deadline || project.target_end_date!).toLocaleDateString()}
                                        </div>
                                      ) : (
                                        <span className="text-sm text-gray-400">—</span>
                                      )}
                                    </td>
                                    <td className="px-6 py-4">
                                      {project.services ? (
                                        <div className="text-sm text-gray-600 line-clamp-1">{project.services}</div>
                                      ) : (
                                        <span className="text-sm text-gray-400">—</span>
                                      )}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right">
                                      <div className="flex items-center justify-end space-x-2">
                                        <button className="text-blue-600 hover:text-blue-900 p-1 hover:bg-blue-50 rounded">
                                          <Edit className="h-4 w-4" />
                                        </button>
                                        <button className="text-gray-600 hover:text-red-600 p-1 hover:bg-red-50 rounded">
                                          <Trash2 className="h-4 w-4" />
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              );
            })()}

            {/* Empty State for Client with No Projects */}
            {expandedClients.has(client.id) && client.projects.length === 0 && (
              <div className="bg-gray-50 border-t border-gray-200 px-6 py-8 text-center">
                <FolderKanban className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                <p className="text-sm text-gray-500 mb-4">No projects for this client yet</p>
                <button
                  onClick={() => handleAddProject(client.id)}
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add First Project
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Empty State - No Clients */}
      {clients.length === 0 && (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <Building2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No clients yet</h3>
          <p className="text-gray-500 mb-6">Get started by adding your first client</p>
          <button
            onClick={() => setShowAddClientModal(true)}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            <Plus className="h-5 w-5 mr-2" />
            Add Client
          </button>
        </div>
      )}

      {/* Add Client Modal */}
      {showAddClientModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900">Add New Client</h3>
              <button
                onClick={() => setShowAddClientModal(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <form onSubmit={handleCreateClient}>
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Client Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="ABC Corporation"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Company
                    </label>
                    <input
                      type="text"
                      name="company"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="ABC Corp"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="contact@abccorp.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Phone
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="(555) 123-4567"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Website
                  </label>
                  <input
                    type="url"
                    name="website"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="https://abccorp.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes
                  </label>
                  <textarea
                    name="notes"
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Additional notes about this client..."
                  />
                </div>
              </div>
              <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowAddClientModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                >
                  Add Client
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Project Modal */}
      {showAddProjectModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
          <div className="relative bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 my-8">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-xl font-semibold text-gray-900">Add New Project</h3>
              <button
                onClick={() => {
                  setShowAddProjectModal(false);
                  setSelectedClientId(null);
                }}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <form onSubmit={handleCreateProject}>
              <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Project Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Website Redesign Project"
                    />
                  </div>
                  {!selectedClientId && (
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Client *
                      </label>
                      <select
                        name="client_id"
                        required
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">Select a client...</option>
                        {clients.map((client) => (
                          <option key={client.id} value={client.id}>
                            {client.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <textarea
                      name="description"
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Project description..."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Status
                    </label>
                    <select
                      name="status"
                      defaultValue="planning"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="planning">Planning</option>
                      <option value="active">Active</option>
                      <option value="on_hold">On Hold</option>
                      <option value="completed">Completed</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Priority
                    </label>
                    <select
                      name="priority"
                      defaultValue="medium"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Owner
                    </label>
                    <input
                      type="text"
                      name="owner"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Project owner name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Services
                    </label>
                    <input
                      type="text"
                      name="services"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Design, Development, SEO"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Start Date
                    </label>
                    <input
                      type="date"
                      name="start_date"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Target End Date
                    </label>
                    <input
                      type="date"
                      name="target_end_date"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Long-term Deadline
                    </label>
                    <input
                      type="date"
                      name="long_term_deadline"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Dependent On (Project ID)
                    </label>
                    <input
                      type="number"
                      name="dependent_on"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Optional project dependency"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Notes
                    </label>
                    <textarea
                      name="notes"
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Additional project notes..."
                    />
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddProjectModal(false);
                    setSelectedClientId(null);
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                >
                  Add Project
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
